import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import {
  CHART_BODY_FONT,
  CHART_TITLE_FONT,
  CHART_X_TICKS_CONFIG,
  CHART_Y_TICKS_CONFIG
} from "components";
import { t } from "i18next";
import moment from "moment";

import { ProductLifetimeStatsPoint } from "../../../apis";
import {
  LIFETIME_FLOW_SERIES,
  LifetimeChartSeriesId,
  ORDERED_SERIES,
  STOCK_BALANCE_COLOR
} from "../constants";

interface UseLifetimeChartProps {
  data: ProductLifetimeStatsPoint[];
  breakdownBy: "Day" | "Month";
  selectedIndex: number | null;
  hiddenSeries: LifetimeChartSeriesId[];
  onSelectIndex: (index: number) => void;
}

const STOCK_BALANCE_DATASET_INDEX = 0;

const getLabelFormat = (breakdownBy: "Day" | "Month") =>
  breakdownBy === "Month" ? "MMM YYYY" : "D MMM";

const getTitleFormat = (breakdownBy: "Day" | "Month") =>
  breakdownBy === "Month" ? "MMMM YYYY" : "D MMM YYYY";

const useLifetimeChart = ({
  data,
  breakdownBy,
  selectedIndex,
  hiddenSeries,
  onSelectIndex
}: UseLifetimeChartProps) => {
  const labels = data.map((point) =>
    moment(point.date).format(getLabelFormat(breakdownBy))
  );

  const state: ChartData<"bar" | "line", number[], string> = {
    labels,
    datasets: [
      {
        type: "line" as const,
        label: t("Stock balance"),
        data: data.map((point) => point.stockBalance),
        yAxisID: "yBalance",
        borderColor: STOCK_BALANCE_COLOR,
        backgroundColor: STOCK_BALANCE_COLOR,
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.3,
        // let hover points at the chart edges render fully instead of
        // being clipped to the chart area
        clip: 8,
        hidden: hiddenSeries.includes("stockBalance")
      },
      {
        type: "line" as const,
        label: t(ORDERED_SERIES.label),
        data: data.map((point) => point.ordered),
        yAxisID: "yFlows",
        borderColor: ORDERED_SERIES.color,
        backgroundColor: ORDERED_SERIES.color,
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.3,
        clip: 8,
        hidden: hiddenSeries.includes(ORDERED_SERIES.key)
      },
      ...LIFETIME_FLOW_SERIES.map((series) => ({
        type: "bar" as const,
        label: t(series.label),
        // outflows are negated so their bars stack down from the zero line
        data: data.map((point) =>
          series.direction === "out" ? -point[series.key] : point[series.key]
        ),
        yAxisID: "yFlows",
        stack: "flows",
        backgroundColor: series.color,
        hoverBackgroundColor: series.color,
        borderRadius: 3,
        borderSkipped: false as const,
        maxBarThickness: 24,
        hidden: hiddenSeries.includes(series.key)
      }))
    ]
  };

  const getBreakdownLines = (index: number): string[] => {
    const point = data[index];
    if (!point) return [];

    const lines: string[] = [];
    const { damaged, returned } = point.breakdown;

    if (point.damaged > 0) {
      lines.push(
        `${t("Damaged")} — ${t("On arrival")}: ${damaged.free} · ${t(
          "In box"
        )}: ${damaged.box} · ${t("From return")}: ${damaged.order}`
      );
    }

    if (point.returned > 0) {
      lines.push(
        `${t("Returned")} — ${t("Replaced")}: ${returned.replaced} · ${t(
          "Cannot be replaced"
        )}: ${returned.cannotBeReplaced} · ${t("Not defective")}: ${
          returned.notDefective
        }`
      );
    }

    return lines;
  };

  const options: ChartOptions<"bar" | "line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false
    },
    onClick: (_event, elements) => {
      if (elements.length > 0) {
        onSelectIndex(elements[0].index);
      }
    },
    plugins: {
      legend: { display: false },
      selectedDay: { index: selectedIndex },
      tooltip: {
        backgroundColor: "#1b1d21",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        footerColor: "#b8bcc4",
        titleFont: CHART_TITLE_FONT,
        bodyFont: CHART_BODY_FONT,
        footerFont: {
          family: "'Eudoxus Sans', sans-serif",
          size: 10,
          weight: 400
        },
        padding: { top: 8, right: 12, bottom: 8, left: 12 },
        // keep the stock balance row (0 is meaningful there), drop zero rows
        // of the other series so sparse days don't produce a wall of zeros
        filter: (item: TooltipItem<"bar" | "line">) =>
          item.datasetIndex === STOCK_BALANCE_DATASET_INDEX ||
          item.parsed.y !== 0,
        callbacks: {
          title: ([item]: TooltipItem<"bar" | "line">[]) =>
            item && data[item.dataIndex]
              ? moment(data[item.dataIndex].date).format(
                  getTitleFormat(breakdownBy)
                )
              : "",
          // outflow bars hold negated values — show them as plain quantities
          label: (item: TooltipItem<"bar" | "line">) =>
            `${item.dataset.label}: ${Math.abs(item.parsed.y ?? 0)}`,
          afterBody: (items: TooltipItem<"bar" | "line">[]) =>
            items[0] ? getBreakdownLines(items[0].dataIndex) : []
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { ...CHART_X_TICKS_CONFIG, autoSkip: true, maxTicksLimit: 12 },
        border: { display: false }
      },
      yFlows: {
        stacked: true,
        position: "left",
        grid: {
          color: (context) =>
            context.tick.value === 0 ? "#e2e4e7" : "#f6f6fa"
        },
        border: { display: false },
        ticks: {
          ...CHART_Y_TICKS_CONFIG,
          // quantities are integers — without this small ranges produce
          // fractional ticks that all round to the same label
          precision: 0,
          // the axis is diverging, so ticks below zero are still quantities
          callback: (value: number | string) => Math.abs(+value)
        }
      },
      yBalance: {
        position: "right",
        beginAtZero: true,
        grid: { display: false },
        border: { display: false },
        ticks: { ...CHART_Y_TICKS_CONFIG, precision: 0 }
      }
    }
  };

  return { state, options };
};

export default useLifetimeChart;
